// frontend/src/modules/payroll/pages/PayrollPage.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Calendar, Banknote, FileDown, ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useApi } from '../../../design-system/useApi';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { runStatusView, isRunCommitted } from '../runStatus';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { payrollApi } from '../api';
import { CreateRunDialog } from '../components/CreateRunDialog';
import { RecurringComponentsPanel } from '../components/RecurringComponentsPanel';
import { TaxRulesPanel } from '../components/TaxRulesPanel';
import { PayrollSettingsPanel } from '../components/PayrollSettingsPanel';
import type { PayrollRun, Payslip } from '../types';

export function PayrollPage() {
  const runsQ = useApi<{ runs: PayrollRun[] }>('/api/payroll/runs');
  const isOwnerOrManager = runsQ.error == null;

  if (!isOwnerOrManager) return <MyPayslipsView />;
  return <RunListView runsQ={runsQ} />;
}

/* ─────────────────────────── Owner / manager view ─────────────────────────── */

type PayrollTab = 'runs' | 'recurring' | 'tax' | 'settings';

const TABS: Array<{ key: PayrollTab; label: string }> = [
  { key: 'runs', label: 'Runs' },
  { key: 'recurring', label: 'Recurring' },
  { key: 'tax', label: 'Tax rules' },
  { key: 'settings', label: 'Settings' },
];

function RunListView({ runsQ }: { runsQ: ReturnType<typeof useApi<{ runs: PayrollRun[] }>> }) {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<PayrollTab>('runs');
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  const runs = runsQ.data?.runs ?? [];

  // A paid run is still a committed run — it just moved one step further, so it
  // keeps counting here instead of silently vanishing from the KPI.
  const committed = runs.filter((r) => isRunCommitted(r)).length;
  const paid = runs.filter((r) => r.status === 'paid').length;
  const drafts = runs.filter((r) => r.status === 'draft').length;
  const distributed = runs.filter((r) => !!r.distributed_at).length;

  return (
    <div>
      <PageHeader
        eyebrow="Team / Payroll"
        title="Payroll runs"
        description="Cut a run, finalize the math, email payslips to everyone in one move."
        actions={
          tab === 'runs' ? (
            <Button
              onClick={() => setOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              New run
            </Button>
          ) : undefined
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-full bg-muted/50 ring-1 ring-border/40 p-1 w-fit" role="tablist" aria-label="Payroll sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={[
                'h-8 px-4 rounded-full text-[12.5px] font-semibold transition-colors',
                tab === t.key
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'recurring' && <RecurringComponentsPanel currency={currency} />}
        {tab === 'tax' && <TaxRulesPanel currency={currency} />}
        {tab === 'settings' && <PayrollSettingsPanel />}

        {tab === 'runs' && (
          <>
        <section className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Kpi label="Total runs" value={runs.length} caption="All-time" icon={<Calendar className="h-3.5 w-3.5" />} />
          <Kpi label="Finalized" value={committed} caption={`${paid} paid · ${distributed} distributed`} icon={<Banknote className="h-3.5 w-3.5" />} tone="success" />
          <Kpi label="Drafts" value={drafts} caption="Open to edit" icon={<FileDown className="h-3.5 w-3.5" />} tone={drafts > 0 ? 'saffron' : 'muted'} />
        </section>

        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          {runsQ.loading ? (
            <RowSkeleton />
          ) : runs.length === 0 ? (
            <EmptyRuns onCreate={() => setOpen(true)} />
          ) : (
            <ul className="divide-y divide-border/40">
              {runs.map((r, i) => (
                <motion.li
                  key={r.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.025, 0.18) }}
                >
                  <Link
                    to={`/dashboard/payroll/runs/${r.id}`}
                    className="flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
                  >
                    <div className="h-11 w-11 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center shrink-0">
                      <Banknote className="h-4 w-4 text-saffron-foreground" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">
                        {formatDate(r.period_start)} – {formatDate(r.period_end)}
                      </p>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {r.finalized_at ? `Finalized ${formatDate(r.finalized_at)}` : 'Open draft'}
                        {r.paid_at ? ` · Paid ${formatDate(r.paid_at)}` : ''}
                        {r.distributed_at ? ` · Distributed ${formatDate(r.distributed_at)}` : ''}
                        {r.notes ? ` · ${r.notes}` : ''}
                      </p>
                    </div>
                    {statusBadge(r)}
                  </Link>
                </motion.li>
              ))}
            </ul>
          )}
        </section>
          </>
        )}
      </div>
      <CreateRunDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={(r) => { setOpen(false); runsQ.reload(); toast.success(`Run created for ${r.period_start}`); }}
      />
    </div>
  );
}

/* ─────────────────────────── Employee view ─────────────────────────── */

function MyPayslipsView() {
  const { data, loading } = useApi<{ payslips: Payslip[] }>('/api/payroll/me/payslips');
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  const rows = data?.payslips ?? [];

  const lifetime = rows.reduce((s, r) => s + Number(r.net_amount), 0);
  const ytd = rows.reduce((s, r) => {
    const t = new Date(r.delivered_at ?? '').getTime();
    if (!Number.isFinite(t)) return s;
    return new Date(t).getFullYear() === new Date().getFullYear() ? s + Number(r.net_amount) : s;
  }, 0);

  return (
    <div>
      <PageHeader
        eyebrow="My account"
        title="My payslips"
        description="Your finalized payslips, ordered by period."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Kpi label="Lifetime earnings" value={formatMoney(lifetime, currency)} caption={`${rows.length} payslip${rows.length === 1 ? '' : 's'}`} icon={<Banknote className="h-3.5 w-3.5" />} stringValue />
          <Kpi label="Year to date" value={formatMoney(ytd, currency)} caption={`${new Date().getFullYear()}`} icon={<Calendar className="h-3.5 w-3.5" />} tone="saffron" stringValue />
        </section>

        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          {loading ? <RowSkeleton /> : rows.length === 0 ? (
            <div className="py-14 px-8 text-center max-w-md mx-auto">
              <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-muted/50 ring-1 ring-border/50 flex items-center justify-center">
                <Banknote className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">No payslips yet</p>
              <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
                Your payslips appear here once a payroll cycle has been run and distributed.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border/40">
              {rows.map((r, i) => (
                <motion.li
                  key={r.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.025, 0.18) }}
                  className="flex items-center gap-4 px-5 sm:px-6 py-4"
                >
                  <div className="h-11 w-11 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center shrink-0">
                    <Banknote className="h-4 w-4 text-saffron-foreground" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground">
                      Net pay {formatMoney(r.net_amount, currency)}
                    </p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      + {formatMoney(r.gross_additions, currency)} additions · − {formatMoney(r.gross_deductions, currency)} deductions
                      {r.delivered_at ? ` · emailed ${formatDate(r.delivered_at)}` : ' · awaiting delivery'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      payrollApi.downloadPayslipPdf(r.id).catch((err: any) =>
                        toast.error('Download failed', { description: err?.message ?? String(err) }),
                      )
                    }
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full ring-1 ring-border/60 text-[12.5px] font-semibold text-foreground hover:ring-border/90 transition-colors"
                  >
                    <FileDown className="h-3.5 w-3.5" /> PDF
                    <ArrowUpRight className="h-3 w-3 opacity-60" />
                  </button>
                </motion.li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/* ─────────────────────────── shared ─────────────────────────── */

function Kpi({
  label, value, caption, icon, tone = 'neutral', stringValue = false,
}: {
  label: string; value: React.ReactNode; caption: string; icon: React.ReactNode;
  tone?: 'neutral' | 'success' | 'saffron' | 'muted'; stringValue?: boolean;
}) {
  const toneClass =
    tone === 'success' ? 'bg-success/10 text-success ring-success/25' :
    tone === 'saffron' ? 'bg-saffron/15 text-saffron-foreground ring-saffron/30' :
    tone === 'muted'   ? 'bg-muted text-muted-foreground ring-border/50' :
                         'bg-muted text-muted-foreground ring-border/50';
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span className={['inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1', toneClass].join(' ')}>{icon}</span>
      </div>
      <p
        className={
          stringValue
            ? 'font-sans font-semibold text-[clamp(1.5rem,2.4vw,2.2rem)] leading-none tracking-[-0.03em] tabular-nums text-foreground'
            : 'font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground'
        }
      >
        {value}
      </p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function RowSkeleton() {
  return (
    <ul className="divide-y divide-border/40">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-11 w-11 rounded-2xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
          </div>
          <div className="h-5 w-20 rounded-full bg-muted/60 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

function EmptyRuns({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="py-14 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
        <Banknote className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
      </div>
      <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">Run your first payroll</p>
      <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
        Pick a pay period, we pull attendance from HR, apply your penalty rules, and email PDF payslips to everyone.
      </p>
      <div className="mt-6">
        <Button onClick={onCreate} className="rounded-full h-10 px-5 font-semibold gap-1.5">
          <Plus className="h-3.5 w-3.5" /> New run
        </Button>
      </div>
    </div>
  );
}

function statusBadge(r: PayrollRun): React.ReactNode {
  const { tone, label } = runStatusView(r);
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {label}
    </span>
  );
}
